import { ChevronLeft, MoreHorizontal } from "lucide-react";
import { useMemo } from "react";

const playfairItalic = "'Playfair Display', Georgia, serif";
const inter = "'Inter', system-ui, sans-serif";

interface BlogMobilePreviewProps {
  title: string;
  description: string;
  categoryName: string | null;
  heroImageUrl: string | null;
  bodyJson: string | null;
  authorName: string | null;
  authorAvatarUrl: string | null;
  publishedAt: string | null;
  relatedBlogs?: Array<{
    id: string;
    title: string;
    thumbnailUrl: string | null;
    categoryName: string | null;
    authorName: string | null;
    readTimeMinutes: number;
  }>;
}

interface ContentBlock {
  id: string;
  type: "heading" | "paragraph" | "quote" | "divider" | "image" | "link";
  content: string;
  level?: number;
  text?: string;
}

function parseBody(raw: string | null): ContentBlock[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    if (raw.trim()) {
      return [{ id: "p-fallback", type: "paragraph", content: raw }];
    }
  }
  return [];
}

function renderInlineBold(text: string): JSX.Element[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <span key={i} className="font-semibold" style={{ color: "#101010" }}>
          {part.slice(2, -2)}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function calcReadTime(blocks: ContentBlock[]): number {
  const words = blocks
    .filter((b) => b.type !== "image" && b.type !== "divider")
    .reduce(
      (acc, b) => acc + (b.content?.split(/\s+/).filter(Boolean).length || 0),
      0,
    );
  return Math.max(1, Math.round(words / 200));
}

function formatPublishDate(iso: string | null): string {
  const date = iso ? new Date(iso) : new Date();
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function renderItemBlock(b: ContentBlock, idx: number) {
  if (b.type === "paragraph") {
    return (
      <p
        key={b.id || idx}
        className="text-[14px]"
        style={{
          color: "#353535",
          lineHeight: "22px",
          letterSpacing: "-0.14px",
        }}
      >
        {renderInlineBold(b.content)}
      </p>
    );
  }
  if (b.type === "quote") {
    return (
      <blockquote
        key={b.id || idx}
        className="border-l-2 pl-4"
        style={{
          borderColor: "#101010",
          fontFamily: playfairItalic,
          fontStyle: "italic",
          fontSize: 18,
          lineHeight: "26px",
          color: "#101010",
        }}
      >
        {b.content}
      </blockquote>
    );
  }
  if (b.type === "image" && b.content) {
    return (
      <div
        key={b.id || idx}
        className="overflow-hidden rounded-[16px]"
        style={{ aspectRatio: "16/10" }}
      >
        <img src={b.content} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }
  if (b.type === "link" && /^(https?:\/\/|www\.)/i.test(b.content.trim())) {
    const url = b.content.trim();
    return (
      <a
        key={b.id || idx}
        href={/^https?:\/\//i.test(url) ? url : `https://${url}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#C2185B", textDecoration: "underline", fontSize: 16 }}
      >
        {b.text?.trim() || url}
      </a>
    );
  }
  if (b.type === "divider") {
    return (
      <hr
        key={b.id || idx}
        className="border-t"
        style={{ borderColor: "#E5E5E5" }}
      />
    );
  }
  return null;
}

function BodyBlocks({ blocks }: { blocks: ContentBlock[] }) {
  // Group into sections: optional heading + cluster of paragraph/quote/image/divider items.
  const sections: { heading: ContentBlock | null; items: ContentBlock[] }[] =
    [];
  let current: { heading: ContentBlock | null; items: ContentBlock[] } = {
    heading: null,
    items: [],
  };
  for (const b of blocks) {
    if (b.type === "heading") {
      if (current.heading || current.items.length) sections.push(current);
      current = { heading: b, items: [] };
    } else {
      current.items.push(b);
    }
  }
  if (current.heading || current.items.length) sections.push(current);

  return (
    <div
      className="px-4 pt-6 pb-10 flex flex-col gap-10"
      data-testid="container-body"
    >
      {sections.map((section, sIdx) => (
        <section key={sIdx} className="flex flex-col gap-3">
          {section.heading && (
            <h2
              style={{
                fontFamily: playfairItalic,
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 28,
                lineHeight: "36px",
                letterSpacing: "-0.28px",
                color: "#101010",
              }}
            >
              {section.heading.content}
            </h2>
          )}
          {section.items.length > 0 && (
            <div className="flex flex-col gap-5">
              {section.items.map((b, idx) => renderItemBlock(b, idx))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

export function BlogMobilePreview({
  title,
  description,
  categoryName,
  heroImageUrl,
  bodyJson,
  authorName,
  authorAvatarUrl,
  publishedAt,
  relatedBlogs = [],
}: BlogMobilePreviewProps) {
  const blocks = useMemo(() => parseBody(bodyJson), [bodyJson]);
  const readTime = useMemo(() => calcReadTime(blocks), [blocks]);
  const dateLabel = useMemo(
    () => formatPublishDate(publishedAt),
    [publishedAt],
  );

  return (
    <div
      className="bg-white w-full mx-auto shadow-xl"
      style={{ maxWidth: 393, fontFamily: inter, color: "#101010" }}
      data-testid="blog-mobile-preview"
    >
      {/* Status Bar */}
      <div
        className="flex justify-between items-center px-6 pt-3 pb-2"
        style={{ height: 46 }}
      >
        <span className="text-[15px] font-semibold tracking-tight">9:41</span>
        <div className="flex gap-1.5 items-center">
          <div className="flex gap-0.5 items-end h-3">
            <div className="w-1 h-1.5 bg-black rounded-sm" />
            <div className="w-1 h-2 bg-black rounded-sm" />
            <div className="w-1 h-2.5 bg-black rounded-sm" />
            <div className="w-1 h-3 bg-black rounded-sm" />
          </div>
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
            <path d="M7 8.5 L1.5 3 a8 8 0 0 1 11 0 z" fill="black" />
          </svg>
          <div className="w-6 h-3 border border-black/40 rounded-sm relative">
            <div
              className="absolute inset-0.5 bg-black rounded-[1px]"
              style={{ width: "75%" }}
            />
          </div>
        </div>
      </div>

      {/* Top Nav */}
      <div className="relative flex items-center justify-between px-4 h-[40px] border-b border-[#F0F0F0]">
        <button className="w-6 h-6 flex items-center justify-center" disabled>
          <ChevronLeft className="w-5 h-5 text-[#101010]" strokeWidth={2} />
        </button>
        <button className="w-6 h-6 flex items-center justify-center" disabled>
          <MoreHorizontal className="w-5 h-5 text-[#101010]" />
        </button>
      </div>

      {/* Article Header */}
      <div className="px-4 pt-6 flex flex-col gap-3">
        {categoryName && (
          <p
            className="text-[12px] font-medium uppercase"
            style={{
              color: "#353535",
              letterSpacing: "0.02em",
              lineHeight: "20px",
            }}
            data-testid="text-preview-category"
          >
            {categoryName}
          </p>
        )}

        <div className="flex flex-col gap-1.5">
          <h1
            style={{
              fontFamily: playfairItalic,
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: 32,
              lineHeight: "36px",
              letterSpacing: "-0.32px",
              color: "#101010",
            }}
            data-testid="text-preview-title"
          >
            {title || "Untitled"}
          </h1>

          {description && (
            <p
              className="text-[14px]"
              style={{
                color: "#878787",
                lineHeight: "20px",
                letterSpacing: "-0.14px",
              }}
              data-testid="text-preview-description"
            >
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Meta + Author group */}
      <div className="px-4 pt-3 flex flex-col gap-3">
        <div
          className="flex items-center gap-1.5"
          data-testid="row-preview-meta"
        >
          <span
            className="text-[14px]"
            style={{ color: "#878787", lineHeight: "18px" }}
          >
            {readTime} min read
          </span>
          <span
            className="w-[5px] h-[5px] rounded-full"
            style={{ background: "#878787" }}
          />
          <span
            className="text-[14px]"
            style={{ color: "#878787", lineHeight: "18px" }}
          >
            {dateLabel}
          </span>
        </div>

        {authorName && (
          <div
            className="flex items-center gap-2.5"
            data-testid="row-preview-author"
          >
            <div className="w-8 h-8 rounded-full overflow-hidden bg-[#EEEEEE] flex-shrink-0">
              {authorAvatarUrl ? (
                <img
                  src={authorAvatarUrl}
                  alt={authorName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[12px] font-semibold text-[#878787]">
                  {authorName.charAt(0)}
                </div>
              )}
            </div>
            <span
              className="text-[16px] font-medium whitespace-nowrap"
              style={{ color: "#101010", lineHeight: "18px" }}
            >
              {authorName}
            </span>
            <button
              className="rounded-[20px] text-[12px] font-medium backdrop-blur-[10px]"
              style={{
                background: "#EEEEEE",
                color: "#353535",
                lineHeight: "14px",
                padding: "9px 12px",
              }}
              disabled
            >
              Read more
            </button>
          </div>
        )}
      </div>

      {/* Hero image */}
      {heroImageUrl && (
        <div className="px-4 pt-6">
          <div
            className="relative overflow-hidden rounded-[16px]"
            style={{ height: 200 }}
            data-testid="img-preview-hero"
          >
            <img
              src={heroImageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{ background: "rgba(0,0,0,0.3)" }}
            />
          </div>
        </div>
      )}

      {/* Body */}
      {blocks.length > 0 ? (
        <BodyBlocks blocks={blocks} />
      ) : (
        <div
          className="px-4 py-10 text-center text-[13px]"
          style={{ color: "#878787" }}
        >
          Add body content to see it rendered here.
        </div>
      )}

      {/* Related Insights */}
      {relatedBlogs.length > 0 && (
        <div className="px-4 pb-10 space-y-5">
          <h3
            className="text-[24px] font-medium tracking-tight"
            style={{
              color: "#000",
              fontFamily: inter,
              letterSpacing: "-0.72px",
              lineHeight: "24px",
            }}
          >
            Related Insights
          </h3>
          <div
            className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-2"
            style={{ scrollbarWidth: "none" }}
          >
            {relatedBlogs.map((card) => (
              <div
                key={card.id}
                className="relative flex-shrink-0 rounded-[16px] overflow-hidden bg-[#1a1a1a]"
                style={{ width: 300, height: 180 }}
                data-testid={`card-preview-related-${card.id}`}
              >
                {card.thumbnailUrl && (
                  <img
                    src={card.thumbnailUrl}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                <div
                  className="absolute inset-0"
                  style={{ background: "rgba(0,0,0,0.45)" }}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 text-white">
                  <p
                    className="mb-2 line-clamp-2"
                    style={{
                      fontFamily: playfairItalic,
                      fontStyle: "italic",
                      fontSize: 20,
                      lineHeight: "28px",
                      letterSpacing: "-0.2px",
                    }}
                  >
                    {card.title}
                  </p>
                  <div className="flex items-center gap-1.5 text-[13px] font-medium">
                    <span>{card.readTimeMinutes} min read</span>
                    {card.authorName && (
                      <>
                        <span className="w-[5px] h-[5px] rounded-full bg-white" />
                        <span>
                          by{" "}
                          <span className="underline">{card.authorName}</span>
                        </span>
                      </>
                    )}
                  </div>
                  {card.categoryName && (
                    <p
                      className="text-[12px] uppercase font-medium mt-3"
                      style={{ letterSpacing: "0.02em" }}
                    >
                      {card.categoryName}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Home indicator */}
      <div className="flex justify-center pb-2 pt-2">
        <div className="w-[134px] h-[5px] rounded-full bg-[#101010]" />
      </div>
    </div>
  );
}
